import { Box, Paper, Skeleton, GridLegacy as Grid } from "@mui/material";
import { Suspense } from "react";
import {
  WasteFulfillmentSummaryProvider,
  WasteFulfillmentSummaryFilter,
} from "../WasteFulfillmentSummaryContext";
import { Summary } from "./Summary";

/**
 * Skeleton fallback for Suspense boundary around Summary.
 * Matches the Summary layout structure.
 */
function SummarySkeleton() {
  return (
    <Grid container spacing={3}>
      {/* Key Metrics Row */}
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ padding: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Skeleton
                  variant="text"
                  width={100}
                  height={48}
                  sx={{ mx: "auto" }}
                />
                <Skeleton
                  variant="text"
                  width={120}
                  height={20}
                  sx={{ mx: "auto" }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Skeleton
                  variant="text"
                  width={80}
                  height={48}
                  sx={{ mx: "auto" }}
                />
                <Skeleton
                  variant="text"
                  width={80}
                  height={20}
                  sx={{ mx: "auto" }}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Chart Skeletons */}
      <Grid item lg={6} md={6} xs={12}>
        <Paper elevation={0} sx={{ p: 2, minHeight: 300 }}>
          <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
          <Skeleton variant="circular" width={200} height={200} sx={{ mx: "auto" }} />
        </Paper>
      </Grid>
      <Grid item lg={6} md={6} xs={12}>
        <Paper elevation={0} sx={{ p: 2, minHeight: 300 }}>
          <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
          <Skeleton variant="circular" width={200} height={200} sx={{ mx: "auto" }} />
        </Paper>
      </Grid>
      <Grid item lg={6} md={6} xs={12}>
        <Paper elevation={0} sx={{ p: 2, minHeight: 300 }}>
          <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={200} />
        </Paper>
      </Grid>
      <Grid item lg={6} md={6} xs={12}>
        <Paper elevation={0} sx={{ p: 2, minHeight: 300 }}>
          <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
          <Skeleton variant="circular" width={200} height={200} sx={{ mx: "auto" }} />
        </Paper>
      </Grid>
    </Grid>
  );
}

type SummaryOverviewProps = {
  /**
   * Filter to apply to the summary charts.
   * When provided, charts will reflect the same filters as the data grid.
   * When undefined, internal defaults are used.
   */
  filter?: WasteFulfillmentSummaryFilter;
  /**
   * Whether the "only overdue" filter is active.
   * Used for highlighting the Overdue stat.
   */
  onlyOverdue: boolean;
  /**
   * Callback when the Overdue stat is clicked.
   */
  onOverdueClick: () => void;
  /**
   * Stable function to extract a key:value pair from the current omnisearch string.
   * Passed from parent to avoid creating a duplicate useOmnisearchInput instance.
   */
  getOmnisearchValue: (key: string) => string | undefined;
  /**
   * Immediately update the omnisearch string (no debounce).
   * Used for click-based filter changes in the charts.
   */
  setOmnisearchImmediate: (val: string) => void;
};

/**
 * Wrapper component for the Summary that provides:
 * - Suspense boundary with skeleton fallback
 * - Context provider with useBackgroundQuery
 *
 * This matches the pattern from WasteSubmissionsOverviewProvider/SubmissionStatsOverview.
 */
export function SummaryOverview({
  filter,
  onlyOverdue,
  onOverdueClick,
  getOmnisearchValue,
  setOmnisearchImmediate,
}: SummaryOverviewProps) {
  return (
    <Suspense fallback={<SummarySkeleton />}>
      <WasteFulfillmentSummaryProvider filter={filter}>
        <Summary
          onlyOverdue={onlyOverdue}
          onOverdueClick={onOverdueClick}
          getOmnisearchValue={getOmnisearchValue}
          setOmnisearchImmediate={setOmnisearchImmediate}
        />
      </WasteFulfillmentSummaryProvider>
    </Suspense>
  );
}
